import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getSeverityMeta } from '../../utils/severityUtils.js';
import { shortEventId } from '../../utils/formatEventId.js';
import { humanizeStatus } from '../../utils/humanizeStatus.js';
import { getEventTypeMeta, getInboxTypeChipClass } from '../../constants/eventTypes.js';
import CategoryChip from './CategoryChip.jsx';
import InboxChip from './InboxChip.jsx';
import InboxListItemMeta from './InboxListItemMeta.jsx';
import TruncatedText from './TruncatedText.jsx';
import OwnerChip from '../Team/OwnerChip.jsx';

/**
 * Compact card for the Inbox split list (aims-htl wq-inbox-item).
 *
 * Resting = surface card with a hairline edge; selected = brand border + tint.
 * Every row is single-line and every card has the same number of rows, so the
 * list keeps a uniform height no matter how long a title or status label is —
 * overflow is absorbed by truncation, which in turn exposes a tooltip.
 */
function InboxListItem({ event, selected = false, onSelect }) {
  const { t } = useTranslation('work-queues-app');
  const severityMeta = getSeverityMeta(event.severity);
  const typeMeta = getEventTypeMeta(event.type);
  const typeChipClass = getInboxTypeChipClass(event.type);
  const TypeIcon = typeMeta?.icon;

  const handleClick = useCallback(() => {
    onSelect(event.id);
  }, [event.id, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={selected}
      className={[
        'flex w-full flex-col gap-1.5 rounded-[10px] border px-3 py-2.5 text-left transition-colors',
        selected
          ? 'border-[#2173FF] bg-[#2173FF]/10'
          : 'border-edge bg-surface hover:border-edge-strong hover:bg-surface-hover-subtle',
      ].join(' ')}
    >
      <div className="flex items-start gap-2">
        <span
          className={[
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
            severityMeta.chipClassName,
          ].join(' ')}
          aria-hidden
        >
          {TypeIcon ? <TypeIcon size={13} /> : null}
        </span>
        <div className="min-w-0 flex-1 text-sm font-medium text-content">
          <TruncatedText text={event.title} />
        </div>
        {event.dueLabel && (
          <div className="min-w-0 max-w-[45%] text-[11px] text-content-faint">
            <TruncatedText text={event.dueLabel} className="text-right" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <InboxChip className={severityMeta.chipClassName}>{t(severityMeta.labelKey)}</InboxChip>
        {typeChipClass && typeMeta ? (
          <InboxChip className={typeChipClass}>{t(typeMeta.labelKey)}</InboxChip>
        ) : null}
        {(event.category || event.studio) && (
          <CategoryChip
            category={event.category}
            studio={event.studio}
            extraCount={event.studioExtraCount}
          />
        )}
        {event.isCustomer && (
          <InboxChip shrinkable>
            <TruncatedText text={t('card.customer')} />
          </InboxChip>
        )}
        {event.status && event.status !== 'open' && (
          <InboxChip shrinkable>
            <TruncatedText text={t(`status.${event.status}`, { defaultValue: humanizeStatus(event.status) })} />
          </InboxChip>
        )}
        {event.delegatedFrom && (
          <InboxChip tone="warning" shrinkable>
            <TruncatedText text={t('card.coveringFor', { name: event.delegatedFrom })} />
          </InboxChip>
        )}
        <span
          className="ml-auto shrink-0 pl-1 font-mono text-[10px] text-content-faint"
          title={event.id}
        >
          {shortEventId(event.id)}
        </span>
      </div>

      <InboxListItemMeta event={event} />

      {event.owner !== undefined && (
        <div className="flex items-center">
          <OwnerChip owner={event.owner} />
        </div>
      )}
    </button>
  );
}

export default memo(InboxListItem);
